import * as XLSX from 'xlsx';

/**
 * One column of a downloadable sheet: how it is titled, how wide it opens, and
 * how a row turns into its cell.
 */
export interface SpreadsheetColumn<Row> {
  header: string;
  /** Column width in characters, as `xlsx` counts them. Defaults to 18. */
  width?: number;
  value: (row: Row) => string | number | null | undefined;
}

export interface SpreadsheetOptions<Row> {
  rows: Row[];
  columns: SpreadsheetColumn<Row>[];
  /** Without an extension — the format decides that. */
  filename: string;
  format: 'xlsx' | 'csv';
  /** Tab name inside the workbook; ignored by CSV. */
  sheetName?: string;
  /**
   * Lines written above the column headings — what shaped the data, for a file
   * that leaves the screen which said it. Guarded for a CSV like any cell.
   */
  preamble?: string[][];
}

const DEFAULT_WIDTH = 18;

/**
 * Leading characters that make a spreadsheet treat a cell as a formula rather
 * than as the text somebody typed: `=` and `+` in every tool, `-` because a
 * value like `-2+3` evaluates, and `@` from the Lotus-era syntax Excel still
 * honours. Tested against the value with leading blanks trimmed, since a space
 * before the payload hides it from this check but not from every reader.
 */
const FORMULA_LEAD = /^[=+\-@]/;

/**
 * Makes a string safe to write into a CSV cell.
 *
 * Two separate problems, and the second is the one that bites:
 *
 * A value starting with a formula character is executed rather than shown, and
 * quoting the field does not stop that — measured in Excel: a quoted `=1+1`
 * opens as `2`, a quoted `=HYPERLINK(…)` as a live link. A leading `'` marks it
 * as text instead. The apostrophe is visible in the cell, and that is the better
 * outcome rather than the cost it looks like: the same measurement showed an
 * unguarded `- fixed the login bug` opening as `#NAME?`, the description gone
 * entirely.
 *
 * A value containing a bare CR reads as the end of the record. Every field is
 * quoted on the way out (see `forceQuotes` below), which is what actually keeps
 * such a value in its cell; normalising CR to LF on top of that leaves one kind
 * of line break in the text instead of two, and does not rely on the quoting.
 *
 * Only CSV needs any of this. A workbook cell carries no formula unless the
 * writer emits an `<f>` element, and this file never sets one — measured on the
 * generated `xl/worksheets/sheet1.xml` with `xlsx@0.18.5`. Quoting there would
 * only leave a stray apostrophe in the cell.
 *
 * It matters because these cells hold free text: a time entry's description is
 * validated as a string with a length limit and nothing else, and the file it
 * lands in is opened by accounting.
 */
function csvSafeText(value: string): string {
  const oneCell = value.replace(/\r\n?/g, '\n');
  return FORMULA_LEAD.test(oneCell.trimStart()) ? `'${oneCell}` : oneCell;
}

/** Excel refuses a sheet name longer than this, or one holding : \ / ? * [ ]. */
const SHEET_NAME_LIMIT = 31;

function safeSheetName(name: string): string {
  return (
    name
      .replace(/[:\\/?*[\]]/g, ' ')
      .trim()
      .slice(0, SHEET_NAME_LIMIT) || 'Sheet1'
  );
}

/**
 * Builds a sheet from column definitions and downloads it.
 *
 * The single place that knows about the `xlsx` package. It exists because
 * several screens export tabular data — the People list, the Time Tracker's
 * Reports tab, and the Report Builder's CSV (its workbook goes through ExcelJS,
 * which can style cells) — and each writing its own is how the two employee exports
 * already drifted apart on column names and date formats (HW-679). A column
 * definition carries its own header and width, so a new column cannot be added
 * to one and forgotten in the other's width array.
 *
 * Pass a number for anything that should be summed in a spreadsheet, and a
 * string for anything that should not (an ID, a code, an already-formatted
 * duration). Numbers are written exactly as given; strings are written as given
 * to a workbook, and made safe for a cell on the way into a CSV — see
 * `csvSafeText`, which is why an ID beginning with `-` or `+` arrives there with
 * a leading apostrophe. Empty cells are written as an empty string rather than
 * left absent, so a column never silently disappears for a row.
 */
export function downloadSpreadsheet<Row>({
  rows,
  columns,
  filename,
  format,
  sheetName = 'Export',
  preamble = [],
}: SpreadsheetOptions<Row>): void {
  const csv = format === 'csv';
  // Built from report settings, and a project or type name among them is data.
  const lead = preamble.map((line) => line.map((cell) => (csv ? csvSafeText(cell) : cell)));
  // Headers are literals at both call sites today, but the type says `string` and
  // nothing stops one being built from data — a project name, a custom-field
  // label. They are never numeric, so guarding them costs nothing.
  const header = columns.map((column) => (csv ? csvSafeText(column.header) : column.header));
  const body = rows.map((row) =>
    columns.map((column) => {
      const value = column.value(row);
      if (value === null || value === undefined) return '';
      // Numbers stay numbers so a spreadsheet can sum them; only text can be
      // mistaken for a formula or break out of its cell.
      return csv && typeof value !== 'number' ? csvSafeText(String(value)) : value;
    })
  );

  const sheet = XLSX.utils.aoa_to_sheet([...lead, header, ...body]);
  sheet['!cols'] = columns.map((column) => ({ wch: column.width ?? DEFAULT_WIDTH }));

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, safeSheetName(sheetName));

  // `forceQuotes` is the library's own answer to a value escaping its cell, and it
  // is the whole answer: quoting every field means no separator inside one can
  // split it, whatever a reader is configured to split on. That matters here
  // because Excel splits a .csv on the Windows list separator, which is `;` in
  // Ukrainian and most European locales — so a description holding a `;` used to
  // become two cells, the second of them evaluated. `csvSafeText` above stays
  // responsible for the other half: a quoted field is still executed if it opens
  // with a formula character.
  //
  // It quotes numbers too, and they survive as numbers: measured in Excel, a
  // quoted `2.5` is right-aligned and the hours column still sums, which is the
  // whole reason accounting asked for a spreadsheet.
  // The intersection is the honest type: `forceQuotes` is declared on the CSV
  // serialiser's options, and `writeFile` threads the same object down to it.
  const csvOptions: XLSX.WritingOptions & XLSX.Sheet2CSVOpts = {
    bookType: 'csv',
    forceQuotes: true,
  };
  XLSX.writeFile(book, `${filename}.${format}`, csv ? csvOptions : undefined);
}
